// conversations/helpers/filterHelpers.js

/**
 * Conversation Filter Helpers
 * ---------------------------
 * This file contains helper functions for filtering conversations in the frontend.
 * The filtering logic is designed to align with the backend SQL behavior to ensure
 * consistent results across the application.
 *
 * Key components:
 * 1. getValueFromConversation: Retrieves values from conversation objects, handling
 *    both top-level properties and nested attributes.
 * 2. matchesCondition: Evaluates a single filter condition against a value.
 * 3. matchesFilters: Evaluates a complete filter chain against a conversation.
 * 4. buildJsonLogicRule: Transforms evaluated filters into a JSON Logic frule that
 *    respects SQL-like operator precedence.
 *
 * Filter Structure:
 * -----------------
 * Each filter has the following structure:
 * {
 *   attributeKey: 'status',          // The attribute to filter on
 *   filterOperator: 'equal_to',      // The operator to use (equal_to, contains, etc.)
 *   values: ['open'],                // The values to compare against
 *   queryOperator: 'and'             // How this filter connects to the next one (and/or)
 * }
 *
 * Operator Precedence:
 * --------------------
 * The filter evaluation respects SQL-like operator precedence using JSON Logic:
 * https://www.postgresql.org/docs/17/sql-syntax-lexical.html#SQL-PRECEDENCE
 * 1. First evaluates individual conditions
 * 2. Then applies AND operators (groups consecutive AND conditions)
 * 3. Finally applies OR operators (connects AND groups with OR operations)
 *
 * This means that a filter chain like "A AND B OR C" is evaluated as "(A AND B) OR C",
 * and "A OR B AND C" is evaluated as "A OR (B AND C)".
 *
 * The implementation uses json-logic-js to apply these rules. The JsonLogic format is designed
 * to allow you to share rules (logic) between front-end and back-end code
 * Here we use json-logic-js to transform filter conditions into a nested JSON Logic structure that preserves proper
 * operator precedence, effectively mimicking SQL-like operator precedence.
 *
 * Conversation Object Structure:
 * -----------------------------
 * The conversation object can have:
 * 1. Top-level properties (status, priority, display_id, etc.)
 * 2. Nested properties in additional_attributes (browser_language, referer, etc.)
 * 3. Nested properties in custom_attributes (conversation_type, etc.)
 */
import jsonLogic from 'json-logic-js';
import { coerceToDate } from '@chatwoot/utils';

/**
 * Gets a value from a conversation based on the attribute key
 * @param {Object} conversation - The conversation object
 * @param {String} attributeKey - The attribute key to get the value for
 * @returns {*} - The value of the attribute
 *
 * This function handles various attribute locations:
 * 1. Direct properties on the conversation object (status, priority, etc.)
 * 2. Properties in conversation.additional_attributes (browser_language, referer, etc.)
 * 3. Properties in conversation.custom_attributes (conversation_type, etc.)
 */
const getValueFromConversation = (conversation, attributeKey) => {
  switch (attributeKey) {
    case 'status':
    case 'priority':
    case 'display_id':
    case 'labels':
    case 'created_at':
    case 'last_activity_at':
      return conversation[attributeKey];
    case 'assignee_id':
      return conversation.meta?.assignee?.id;
    case 'inbox_id':
      return conversation.inbox_id;
    case 'team_id':
      return conversation.meta?.team?.id;
    case 'browser_language':
    case 'country_code':
    case 'referer':
      return conversation.additional_attributes?.[attributeKey];
    default:
      // Check if it's a custom attribute
      if (
        conversation.custom_attributes &&
        conversation.custom_attributes[attributeKey]
      ) {
        return conversation.custom_attributes[attributeKey];
      }
      return null;
  }
};

/**
 * Resolves the value from an input candidate
 * @param {*} candidate - The input value to resolve
 * @returns {*} - If the candidate is an object with an id property, returns the id;
 *                otherwise returns the candidate unchanged
 *
 * This helper function is used to normalize values, particularly when dealing with
 * objects that represent entities like users, teams, or inboxes where we want to
 * compare by ID rather than by the whole object.
 */
const resolveValue = candidate => {
  if (
    typeof candidate === 'object' &&
    candidate !== null &&
    'id' in candidate
  ) {
    return candidate.id;
  }

  return candidate;
};

/**
 * Checks if two values are equal in the context of filtering
 * @param {*} filterValue - The filterValue value
 * @param {*} conversationValue - The conversationValue value
 * @returns {Boolean} - Returns true if the values are considered equal according to filtering rules
 *
 * This function handles various equality scenarios:
 * 1. When both values are arrays: checks if all items in filterValue exist in conversationValue
 * 2. When filterValue is an array but conversationValue is not: checks if conversationValue is included in filterValue
 * 3. Otherwise: performs strict equality comparison
 */
/**
 * Checks if two values are equal in the context of filtering
 */
const equalTo = (filterValue, conversationValue) => {
  if (Array.isArray(filterValue)) {
    if (filterValue.includes('all')) {
      return true;
    }
    if (filterValue === 'all') {
      return true;
    }

    if (Array.isArray(conversationValue)) {
      // Para labels: verifica se ALGUM dos valores do filtro existe na conversa
      // Mudança: de .every() para .some() - queremos OR entre os valores do filtro
      const result = filterValue.some(val => {
        const found = conversationValue.includes(val);
        return found;
      });
      return result;
    }

    if (!Array.isArray(conversationValue)) {
      const result = filterValue.includes(conversationValue);
      return result;
    }
  }

  const result = conversationValue === filterValue;
  return result;
};

/**
 * Checks if the filterValue value is contained within the conversationValue value
 * @param {*} filterValue - The value to look for
 * @param {*} conversationValue - The value to search within
 * @returns {Boolean} - Returns true if filterValue is contained within conversationValue
 *
 * This function performs case-insensitive string containment checks.
 * It only works with string values and returns false for non-string types.
 */
const contains = (filterValue, conversationValue) => {
  if (typeof conversationValue === 'string') {
    return conversationValue.toLowerCase().includes(filterValue.toLowerCase());
  }
  return false;
};

const BARE_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Computes the start/end of a "YYYY-MM-DD" day in UTC, as unix seconds.
 *
 * The backend stores conversations.created_at/last_activity_at as UTC
 * ("timestamp without time zone", written with the app's UTC clock — see
 * Time.zone) and day filters cast with `(created_at)::date`, which truncates
 * that UTC wall-clock value. `coerceToDate` (from @chatwoot/utils) parses a
 * bare date string as UTC midnight but then "normalizes" it via
 * `setHours(0,0,0,0)`, which runs in the BROWSER's local timezone and
 * silently shifts the instant by the local UTC offset — e.g. a conversation
 * created at 01:30 UTC on the 10th (day "10" for the backend) reads as the
 * 9th for anyone in UTC-3. Day boundaries here are computed in UTC directly
 * to stay in agreement with the backend regardless of the viewer's timezone.
 */
const DAY_IN_MS = 24 * 60 * 60 * 1000;

/**
 * Start of a "YYYY-MM-DD" day in UTC, in milliseconds — or null when the date
 * does not exist.
 *
 * `Date.UTC` rola componente fora de faixa em silêncio (2026-02-31 vira
 * 2026-03-03), e a lista passaria a mostrar as conversas do dia errado
 * enquanto o backend rejeita o mesmo valor no cast `::date`. Data inválida
 * grita e não casa nada, em vez de fingir um dia próximo.
 */
const utcDayStartMs = dateString => {
  const [year, month, day] = dateString.split('-').map(Number);
  const startMs = Date.UTC(year, month - 1, day);
  const parsed = new Date(startMs);

  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[filterHelpers] Data inválida no filtro: "${dateString}". A condição não vai casar nenhuma conversa.`
    );
    return null;
  }

  return startMs;
};

const getUtcDayBoundarySeconds = dateString => {
  const startMs = utcDayStartMs(dateString);
  if (startMs === null) return null;

  return {
    startTimestamp: Math.floor(startMs / 1000),
    endTimestamp: Math.floor((startMs + DAY_IN_MS - 1) / 1000),
  };
};

/**
 * "YYYY-MM-DD" → Date no início do dia em UTC (null se a data não existe).
 * Só o começo do dia interessa aqui, então não calcula o par start/end.
 */
const bareDateToDate = dateString => {
  const startMs = utcDayStartMs(dateString);
  return startMs === null ? null : new Date(startMs);
};

/**
 * Casamento dia-contra-dia de um timestamp da conversa contra uma data pura do
 * filtro, compartilhado por equal_to e not_equal_to.
 *
 * Em pastas salvas o valor chega como array (['2026-08-10']); ao aplicar um
 * filtro ao vivo, como string. O not_equal_to não desembrulhava, comparava o
 * array cru contra um número e casava TODA conversa.
 *
 * @returns {null} quando o par não é (timestamp × data pura) — o chamador
 *                 segue pelo caminho normal de igualdade
 * @returns {{ matched: Boolean|null }} matched null = data inexistente
 */
const matchBareDateDay = (conversationValue, filterValue) => {
  const dateCandidate = Array.isArray(filterValue)
    ? filterValue[0]
    : filterValue;

  if (
    typeof conversationValue !== 'number' ||
    typeof dateCandidate !== 'string' ||
    !BARE_DATE_REGEX.test(dateCandidate)
  ) {
    return null;
  }

  // UTC, não horário local — precisa concordar com o backend (ver
  // getUtcDayBoundarySeconds acima).
  const boundary = getUtcDayBoundarySeconds(dateCandidate);
  if (boundary === null) return { matched: null };

  return {
    matched:
      conversationValue >= boundary.startTimestamp &&
      conversationValue <= boundary.endTimestamp,
  };
};

/**
 * Compares two date values using a comparison function
 * @param {*} conversationValue - The conversation value to compare
 * @param {*} filterValue - The filter value to compare against
 * @param {Function} compareFn - The comparison function to apply
 * @returns {Boolean} - Returns true if the comparison succeeds, false otherwise
 */
const compareDates = (conversationValue, filterValue, compareFn) => {
  const conversationDate = coerceToDate(conversationValue);

  // In saved views, the filterValue might be returned as an Array
  // In conversation list, when filtering, the filterValue will be returned as a string
  const valueToCompare = Array.isArray(filterValue)
    ? filterValue[0]
    : filterValue;

  const isBareDate =
    typeof valueToCompare === 'string' && BARE_DATE_REGEX.test(valueToCompare);

  // Bare "YYYY-MM-DD" values go through the UTC-safe boundary helper (see
  // above); anything else (ISO strings with time, numeric timestamps) is
  // unaffected by the coerceToDate bug and can use it as-is.
  const filterDate = isBareDate
    ? bareDateToDate(valueToCompare)
    : coerceToDate(valueToCompare);

  if (conversationDate === null || filterDate === null) return false;

  // O backend só compara DIA-CONTRA-DIA quando o filtro é uma data pura
  // (`(created_at)::date > 'YYYY-MM-DD'::date`) — se o filtro tem hora, ele
  // compara timestamp completo. Espelha isso aqui: pra filtro de data pura,
  // trunca o instante da conversa pro seu próprio dia em UTC antes de
  // comparar, senão uma conversa mais cedo no MESMO dia do filtro contava
  // como "maior que" ele (e o backend nunca a devolveria pra essa Pasta).
  const comparisonConversationDate = isBareDate
    ? new Date(
        Date.UTC(
          conversationDate.getUTCFullYear(),
          conversationDate.getUTCMonth(),
          conversationDate.getUTCDate()
        )
      )
    : conversationDate;

  return compareFn(comparisonConversationDate, filterDate);
};

/**
 * Checks if a value matches a filter condition
 * @param {*} conversationValue - The value to check
 * @param {Object} filter - The filter condition
 * @returns {Boolean} - Returns true if the value matches the filter
 */
const matchesCondition = (conversationValue, filter) => {
  const { filter_operator: filterOperator, values } = filter;

  // Handle null/undefined values
  if (conversationValue === null || conversationValue === undefined) {
    return filterOperator === 'is_not_present';
  }

  // Resolver values - se for array, resolve cada item; senão, resolve o valor único
  let filterValue;
  if (Array.isArray(values)) {
    filterValue = values.map(v => {
      // Se o item for objeto com id (ex: {id: "cobranca", name: "cobranca"}), extrai o id
      if (v && typeof v === 'object' && 'id' in v) {
        return v.id;
      }
      return v;
    });
  } else {
    filterValue = resolveValue(values);
  }

  let result;

  switch (filterOperator) {
    case 'equal_to': {
      const dayMatch = matchBareDateDay(conversationValue, filterValue);
      if (dayMatch) return dayMatch.matched === true;

      result = equalTo(filterValue, conversationValue);
      return result;
    }

    case 'not_equal_to': {
      const dayMatch = matchBareDateDay(conversationValue, filterValue);
      // Data inexistente não casa nada NEM negada — senão a condição quebrada
      // passaria a devolver toda conversa da conta.
      if (dayMatch) return dayMatch.matched === false;

      result = !equalTo(filterValue, conversationValue);
      return result;
    }

    case 'contains':
      result = contains(filterValue, conversationValue);
      return result;

    case 'does_not_contain':
      result = !contains(filterValue, conversationValue);
      return result;

    case 'is_present':
      return true;

    case 'is_not_present':
      return false;

    case 'is_greater_than':
      result = compareDates(conversationValue, filterValue, (a, b) => a > b);
      return result;

    case 'is_less_than':
      result = compareDates(conversationValue, filterValue, (a, b) => a < b);
      return result;

    case 'days_before': {
      // O backend calcula isso como `Time.zone.today - N.days` (app roda em
      // UTC) e delega pro mesmo caminho de is_less_than dia-contra-dia (ver
      // compareDates acima) — reconstrói aqui o mesmo cálculo, em vez de
      // comparar direto contra new Date(), que (a) usava fuso local e (b)
      // comparava segundos (conversationValue) com milissegundos
      // (targetDate.getTime()), o que fazia a condição dar sempre verdadeira.
      const daysValue = Number(
        Array.isArray(filterValue) ? filterValue[0] : filterValue
      );
      // Entrada inválida (texto não-numérico = NaN, mas também 1e9 dias, que
      // é finito e estoura a faixa do Date) daria Invalid Date, e o
      // toISOString() abaixo lançaria RangeError — a exceção sobe até o getter
      // e apaga a lista inteira. Os demais operadores tratam entrada inválida
      // retornando false, não estourando.
      if (!Number.isFinite(daysValue)) return false;
      const now = new Date();
      const utcTodayMs = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate()
      );
      const thresholdMs = utcTodayMs - daysValue * DAY_IN_MS;
      // Faixa do Date em JS: ±8.64e15 ms a partir da epoch.
      if (Math.abs(thresholdMs) > 8.64e15) return false;

      const thresholdDateString = new Date(thresholdMs)
        .toISOString()
        .slice(0, 10);
      result = compareDates(
        conversationValue,
        thresholdDateString,
        (a, b) => a < b
      );
      return result;
    }

    default:
      return false;
  }
};

/**
 * Converts an array of evaluated filters into a JSON Logic rule
 * that respects SQL-like operator precedence (AND before OR)
 *
 * This function transforms the linear sequence of filter results and operators
 * into a nested JSON Logic structure that correctly implements SQL-like precedence:
 * - AND operators are evaluated before OR operators
 * - Consecutive AND conditions are grouped together
 * - These AND groups are then connected with OR operators
 *
 * For example:
 * - "A AND B AND C" becomes { "and": [A, B, C] }
 * - "A OR B OR C" becomes { "or": [A, B, C] }
 * - "A AND B OR C" becomes { "or": [{ "and": [A, B] }, C] }
 * - "A OR B AND C" becomes { "or": [A, { "and": [B, C] }] }
 *
 * FILTER CHAIN:  A --AND--> B --OR--> C --AND--> D --AND--> E --OR--> F
 *                |         |         |         |         |         |
 *                v         v         v         v         v         v
 * EVALUATED:    true      false     true      false     true      false
 *                \         /         \         \         /         /
 *                 \       /           \         \       /         /
 *                  \     /             \         \     /         /
 *                   \   /               \         \   /         /
 *                    \ /                 \         \ /         /
 * AND GROUPS:      [true,false]          [true,false,true]    [false]
 *                     |                       |                  |
 *                     v                       v                  v
 * JSON LOGIC:    {"and":[true,false]}   {"and":[true,false,true]}  false
 *                   \                      |                  /
 *                    \                     |                 /
 *                     \                    |                /
 *                      \                   |               /
 *                       \                  |              /
 * FINAL RULE:            {"or":[{"and":[true,false]},{"and":[true,false,true]},false]}
 *
 * {
 *  "or": [
 *    { "and": [true, false] },
 *    { "and": [true, false, true] },
 *    { "and": [false] }
 *  ]
 * }
 * @param {Array} evaluatedFilters - Array of evaluated filter conditions with results and operators
 * @returns {Object} - JSON Logic rule
 */
const buildJsonLogicRule = evaluatedFilters => {
  // Step 1: Group consecutive AND conditions into logical units
  // This implements the higher precedence of AND over OR
  const andGroups = [];
  let currentAndGroup = [evaluatedFilters[0].result];

  for (let i = 0; i < evaluatedFilters.length - 1; i += 1) {
    if (evaluatedFilters[i].operator === 'and') {
      // When we see an AND operator, we add the next filter to the current AND group
      // This builds up chains of AND conditions that will be evaluated together
      currentAndGroup.push(evaluatedFilters[i + 1].result);
    } else {
      // When we see an OR operator, it marks the boundary between AND groups
      // We finalize the current AND group and start a new one

      // If the AND group has only one item, don't wrap it in an "and" operator
      // Otherwise, create a proper "and" JSON Logic expression
      andGroups.push(
        currentAndGroup.length === 1
          ? currentAndGroup[0] // Single item doesn't need an "and" wrapper
          : { and: currentAndGroup } // Multiple items need to be AND-ed together
      );

      // Start a new AND group with the next filter's result
      currentAndGroup = [evaluatedFilters[i + 1].result];
    }
  }

  // Step 2: Add the final AND group that wasn't followed by an OR
  if (currentAndGroup.length > 0) {
    andGroups.push(
      currentAndGroup.length === 1
        ? currentAndGroup[0] // Single item doesn't need an "and" wrapper
        : { and: currentAndGroup } // Multiple items need to be AND-ed together
    );
  }

  // Step 3: Combine all AND groups with OR operators
  // If we have multiple AND groups, they are separated by OR operators
  // in the original filter chain, so we combine them with an "or" operation
  if (andGroups.length > 1) {
    return { or: andGroups };
  }

  // If there's only one AND group (which might be a single condition
  // or multiple AND-ed conditions), just return it directly
  return andGroups[0];
};

/**
 * Evaluates each filter against the conversation and prepares the results array
 * @param {Object} conversation - The conversation to evaluate
 * @param {Array} filters - Filters to apply
 * @returns {Array} - Array of evaluated filter results with operators
 */
const evaluateFilters = (conversation, filters) => {
  return filters.map((filter, index) => {
    const value = getValueFromConversation(conversation, filter.attribute_key);
    const result = matchesCondition(value, filter);

    // This part determines the logical operator that connects this filter to the next one:
    // - If this is not the last filter (index < filters.length - 1), use the filter's query_operator
    //   or default to 'and' if query_operator is not specified
    // - If this is the last filter, set operator to null since there's no next filter to connect to
    const isLastFilter = index === filters.length - 1;
    const operator = isLastFilter ? null : filter.query_operator || 'and';

    return { result, operator };
  });
};

/**
 * Checks if a conversation matches the given filters
 * @param {Object} conversation - The conversation object to check
 * @param {Array} filters - Array of filter conditions
 * @returns {Boolean} - Returns true if conversation matches filters, false otherwise
 */
export const matchesFilters = (conversation, filters) => {
  // If no filters, return true
  if (!filters || filters.length === 0) {
    return true;
  }

  // Handle single filter case
  if (filters.length === 1) {
    const value = getValueFromConversation(
      conversation,
      filters[0].attribute_key
    );

    return matchesCondition(value, filters[0]);
  }

  // Evaluate all conditions and prepare for jsonLogic
  const evaluatedFilters = evaluateFilters(conversation, filters);
  return jsonLogic.apply(buildJsonLogicRule(evaluatedFilters));
};
